import { Link } from 'react-router-dom'
import { ChevronLeft, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PoliticalLabel } from '@/components/ideology/PoliticalLabel'
import type { Party } from '@/types'

interface PartyHeaderProps {
  party: Party
  nationName: string
  nationId: string
  isOwner: boolean
  onEdit: () => void
}

export function PartyHeader({
  party,
  nationName,
  nationId,
  isOwner,
  onEdit,
}: PartyHeaderProps) {
  return (
    <div className="space-y-4">
      {/* Breadcrumb and Edit button row */}
      <div className="flex items-center justify-between">
        <Link
          to={`/nations/${nationId}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {nationName}
        </Link>

        {isOwner && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </div>

      {/* Party identity */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          {/* Color bar */}
          <div
            className="w-2 h-12 rounded-full"
            style={{ backgroundColor: party.color }}
          />

          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">{party.name}</h1>
              <Badge
                variant="outline"
                style={{
                  borderColor: party.color,
                  color: party.color,
                }}
              >
                {party.abbreviation}
              </Badge>
            </div>
          </div>
        </div>

        {/* Slogan */}
        {party.slogan && (
          <p className="text-muted-foreground italic pl-5">
            "{party.slogan}"
          </p>
        )}

        {/* Political Position Label */}
        <div className="pl-5">
          <PoliticalLabel
            ideology={party.ideology_vector}
            showSubLabel={true}
            size="md"
            variant="card"
          />
        </div>
      </div>
    </div>
  )
}
